// ============================================================================
// EmojiPicker — a lightweight, dependency-free native-emoji picker (Wave 5).
// ----------------------------------------------------------------------------
// A compact popover of curated unicode emoji grouped into a few rows. Tapping an
// emoji calls back with the unicode character (the composer inserts it into the
// text — emoji are plain UTF-8, stored/round-tripped verbatim). No images, no
// custom emoji, no third-party bundle: just a scrollable grid the phone renders
// with its own emoji font.
//
// Mobile-first: a thumb-reachable popover anchored above the composer, with a
// fixed height + scroll so it never pushes the input off-screen. Closes on
// outside click / Escape. Labels via i18n keys (messages namespace); the emoji
// themselves are language-neutral. Shared by BOTH messaging realms (the client
// portal thread and the trainer client-detail thread).
// ============================================================================
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

// A curated set — the everyday fitness/chat emoji a coach or client reaches for.
// Kept short so the picker stays one screenful on a phone (no category tabs).
const EMOJI: readonly string[] = [
  "😀", "😁", "😂", "🤣", "😊", "😍", "😎", "🤗",
  "👍", "👏", "🙌", "💪", "🔥", "✨", "⭐", "🎉",
  "❤️", "💯", "🙏", "👌", "😅", "😉", "😴", "🤔",
  "😭", "😤", "😮‍💨", "🥵", "🥳", "🤩", "😇", "🫶",
  "🏃", "🚴", "🏋️", "🧘", "🥗", "🍎", "💧", "⏰",
  "✅", "❌", "⚡", "🌟", "💥", "🎯", "📈", "🥇",
];

export interface EmojiPickerProps {
  onPick: (emoji: string) => void;
  onClose: () => void;
}

export function EmojiPicker({ onPick, onClose }: EmojiPickerProps) {
  const { t } = useTranslation("messages");
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside-click / Escape (the native-app dismiss gesture).
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={t("emoji.title")}
      className="absolute bottom-full left-0 z-40 mb-2 w-64 max-w-[calc(100vw-2rem)] rounded-xl border border-line bg-surface p-2 shadow-lg"
    >
      <div className="grid max-h-48 grid-cols-8 gap-0.5 overflow-y-auto">
        {EMOJI.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => onPick(emoji)}
            aria-label={emoji}
            className="grid h-8 w-8 place-items-center rounded-md text-xl leading-none transition-colors hover:bg-surface-sunken active:scale-90"
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
}
